import React, { useEffect, useMemo, useState } from "react";
import "./Wallet.css";

const API_BASE = "https://xiadot.com/vendor_api";

function money(n) {
  const x = Number(n || 0);
  return "₹" + x.toLocaleString("en-IN", { minimumFractionDigits: 2 });
}

function fmtDate(d) {
  if (!d) return "-";
  const s = String(d);
  return s.length >= 19 ? s.slice(0, 19).replace("T", " ") : s;
}

function statusConfig(s) {
  const raw = String(s || "");
  const x = raw.toLowerCase();
  const label = raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "Pending";
  if (x === "accepted" || x === "delivered" || x === "completed") return { cls: "pw-badge-green", label };
  if (x === "rejected" || x === "cancelled") return { cls: "pw-badge-red", label };
  if (x === "processing") return { cls: "pw-badge-blue", label };
  return { cls: "pw-badge-amber", label };
}

export default function WalletPage() {
  const token = useMemo(
    () =>
      localStorage.getItem("vendor_token") ||
      localStorage.getItem("token") ||
      "",
    [],
  );

  const [balance, setBalance] = useState(0);
  const [pending, setPending] = useState(0);
  const [amount, setAmount] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [recentEarnings, setRecentEarnings] = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);
  const [previewModal, setPreviewModal] = useState(null); // { orderId, imageUrl }

  const loadWallet = async () => {
    try {
      const res = await fetch(`${API_BASE}/vendor/payouts/wallet.php`, {
        method: "GET",
        headers: { Authorization: "Bearer " + token },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false)
        throw new Error(data?.message || "Wallet API failed");
      setBalance(
        Number.isFinite(Number(data?.balance)) ? Number(data.balance) : 0,
      );
      setPending(
        Number.isFinite(Number(data?.pending)) ? Number(data.pending) : 0,
      );
    } catch (e) {
      setErr(String(e.message || e));
      setBalance(0);
      setPending(0);
    }
  };

  const loadHistory = async () => {
    try {
      setLoadingHist(true);
      const res = await fetch(`${API_BASE}/vendor/payouts/history.php`, {
        method: "GET",
        headers: { Authorization: "Bearer " + token },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false)
        throw new Error(data?.message || "History API failed");
      const rows = Array.isArray(data?.rows)
        ? data.rows
        : Array.isArray(data?.payouts)
          ? data.payouts
          : Array.isArray(data)
            ? data
            : [];
      setHistory(rows);
      setRecentEarnings(Array.isArray(data?.recent_earnings) ? data.recent_earnings : []);
    } catch {
      setHistory([]);
      setRecentEarnings([]);
    } finally {
      setLoadingHist(false);
    }
  };

  useEffect(() => {
    setErr("");
    setMsg("");
    loadWallet();
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const requestPayout = async (e) => {
    e?.preventDefault?.();
    setErr("");
    setMsg("");

    const amt = Number(amount);
    const available = Number(balance || 0);

    // ✅ Block if previous request still pending
    if (pendingCount > 0) {
      return setErr("Your previous payout request is still pending. Please wait until it is approved or rejected.");
    }

    if (!amt || amt <= 0) {
      return setErr("Please enter a valid amount");
    }

    if (amt > available) {
      return setErr(
        `Insufficient wallet balance. Available balance is ${money(available)}`
      );
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/vendor/payouts/request.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({ amount: amt }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || "Payout request failed");
      }

      setMsg("Request sent successfully. Admin will approve 👍");
      setAmount("");

      await loadWallet();
      await loadHistory();
    } catch (e) {
      setErr(e.message || "Payout request failed");
    } finally {
      setLoading(false);
    }
  };

  const requestOrderPayout = async (order_id, amount) => {
    if (!window.confirm(`Are you sure you want to request payout for Order #${order_id}?`)) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/vendor/payouts/request.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({ amount: Number(amount), order_id: Number(order_id) }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || "Payout request failed");
      }
      alert("Withdrawal request sent successfully. Admin will approve 👍");
      await loadWallet();
      await loadHistory();
    } catch (e) {
      alert(e.message || "Payout request failed");
    } finally {
      setLoading(false);
    }
  };

  const acceptedTotal = history
    .filter((r) => String(r.status).toLowerCase() === "accepted")
    .reduce((a, r) => a + Number(r.amount || 0), 0);

  const pendingCount = history.filter(
    (r) => String(r.status).toLowerCase() === "pending",
  ).length;

  return (
    <div className="pw-page">
      {/* Header */}
      <div className="pw-header">
        <div className="pw-header-left">
          <div className="pw-header-icon">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <line x1="2" y1="10" x2="22" y2="10" />
            </svg>
          </div>
          <div>
            <h1 className="pw-title">Payouts</h1>
            <p className="pw-subtitle">
              Manage your wallet and payout requests
            </p>
          </div>
        </div>
        <button
          className="pw-refresh-btn"
          onClick={() => {
            loadWallet();
            loadHistory();
          }}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Stats Row */}
      <div className="pw-stats">
        <div className="pw-stat pw-stat-navy">
          <div className="pw-stat-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <line x1="2" y1="10" x2="22" y2="10" />
            </svg>
          </div>
          <div className="pw-stat-body">
            <div className="pw-stat-label">Available Balance</div>
            <div className="pw-stat-val">{money(balance)}</div>
          </div>
        </div>

        <div className="pw-stat pw-stat-amber">
          <div className="pw-stat-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div className="pw-stat-body">
            <div className="pw-stat-label">Pending Amount</div>
            <div className="pw-stat-val">{money(pending)}</div>
          </div>
        </div>

        <div className="pw-stat pw-stat-green">
          <div className="pw-stat-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div className="pw-stat-body">
            <div className="pw-stat-label">Total Paid Out</div>
            <div className="pw-stat-val">{money(acceptedTotal)}</div>
          </div>
        </div>

        <div className="pw-stat pw-stat-blue">
          <div className="pw-stat-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
          </div>
          <div className="pw-stat-body">
            <div className="pw-stat-label">Pending Requests</div>
            <div className="pw-stat-val">{pendingCount}</div>
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="pw-grid">
        {/* Request Payout */}


        {/* RIGHT COLUMN */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          
          {/* Order Earnings History */}
          <div className="pw-card pw-history-card">
            <div className="pw-card-head">
              <div className="pw-card-icon pw-card-icon-green" style={{ background: "var(--green-light)", color: "var(--green)" }}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div>
                <div className="pw-card-title">Order Earnings</div>
                <div className="pw-card-sub">Recent earnings per order</div>
              </div>
            </div>

            {loadingHist ? (
              <div className="pw-state">
                <div className="pw-spinner pw-spinner-lg" />
                <p>Loading earnings…</p>
              </div>
            ) : recentEarnings.length === 0 ? (
              <div className="pw-state">
                <div className="pw-empty-icon">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <p className="pw-empty-title">No recent earnings</p>
                <p className="pw-empty-sub">
                  Your order earnings will appear here
                </p>
              </div>
            ) : (
              <div className="pw-table-wrap">
                <table className="pw-table">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Product</th>
                      <th>MRP</th>
                      <th>Qty</th>
                      <th>Amount Earned</th>
                      <th>Order Status</th>
                      <th>Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentEarnings.map((er, idx) => {
                      const st = statusConfig(er.order_status);
                      return (
                        <tr key={er.earning_id ?? idx}>
                          <td className="pw-td-num">{er.order_code}</td>
                          <td>{er.product_name}</td>
                          <td className="pw-td-amount">{money(er.unit_price)}</td>
                          <td>{er.quantity}</td>
                          <td className="pw-td-amount">{money(er.net_earning)}</td>
                          <td>
                            <span className={`pw-badge ${st.cls}`}>
                              <span className="pw-badge-dot" />
                              {er.order_status || 'Pending'}
                            </span>
                            {er.order_status === "DELIVERED" && (er.delivery_image || er.delivery_video) && (
                              <div style={{ marginTop: 8 }}>
                                <button
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 4,
                                    fontSize: 11,
                                    color: "#2563eb",
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: 0,
                                    textDecoration: "underline"
                                  }}
                                  onClick={() => setPreviewModal({ 
                                    orderId: er.order_id || er.order_code, 
                                    imageUrl: er.delivery_image ? `${API_BASE}/${er.delivery_image}` : null,
                                    videoUrl: er.delivery_video ? `${API_BASE}/${er.delivery_video}` : null
                                  })}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                  </svg>
                                  View Proof
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="pw-td-date">{fmtDate(er.earned_at || er.order_date)}</td>
                          <td>
                            {er.payout_status ? (
                              <span className={`pw-badge ${statusConfig(er.payout_status).cls}`}>
                                <span className="pw-badge-dot" />
                                {er.payout_status}
                              </span>
                            ) : String(er.order_status).toLowerCase() !== 'delivered' ? (
                              <span style={{ fontSize: '11.5px', color: '#94a3b8', fontWeight: 500 }}>
                                Deliver to withdraw
                              </span>
                            ) : (
                              <button 
                                onClick={() => requestOrderPayout(er.order_id, er.net_earning)}
                                className="pw-submit-btn"
                                style={{ height: '32px', padding: '0 12px', fontSize: '12px' }}
                                disabled={loading}
                              >
                                Withdraw
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── Image preview modal ── */}
      {previewModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={() => setPreviewModal(null)}>
          <div style={{ background: "#fff", padding: 20, borderRadius: 12, width: "100%", maxWidth: 500, boxShadow: "0 10px 25px rgba(0,0,0,0.2)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Delivery Proof (Order {String(previewModal.orderId).padStart(4, "0")})</h3>
              <button
                style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#666" }}
                onClick={() => setPreviewModal(null)}
              >
                &times;
              </button>
            </div>
            <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: "16px", maxHeight: "70vh", overflowY: "auto", padding: "10px" }}>
              {previewModal.imageUrl && (
                <img 
                  src={previewModal.imageUrl} 
                  alt="Delivery Proof" 
                  style={{ maxWidth: "100%", maxHeight: 400, borderRadius: 12, objectFit: "contain", border: "1px solid #eee", margin: "0 auto" }} 
                />
              )}
              {previewModal.videoUrl && (
                <video 
                  src={previewModal.videoUrl} 
                  controls 
                  style={{ maxWidth: "100%", maxHeight: 400, borderRadius: 12, border: "1px solid #eee", margin: "0 auto" }} 
                />
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button style={{ padding: "8px 16px", border: "1px solid #ddd", background: "#fff", borderRadius: 6, cursor: "pointer", fontWeight: 500 }} onClick={() => setPreviewModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
